import { DocumentNode, MutationFunctionOptions, MutationHookOptions, MutationResult, useMutation } from '@apollo/client'
import ClientLogger from '@eversports/react-components/utilities/client-logger'
import { ExecutionResult } from 'graphql'
import { useEffect } from 'react'

import useCheckoutContext from './useCheckoutContext'

// See https://github.com/microsoft/TypeScript/issues/15300#issuecomment-371353444 why this cannot be typed nicely
// with and index signature
//
// interface CustomMutationResponseSignature {
//   [key: string]: Data | ExpectedErrors
// }
interface ExpectedError {
  id?: string
  path?: string
  message?: string
}
interface ExpectedErrors {
  __typename: 'ExpectedErrors'
  errors?: Array<ExpectedError>
}

interface Data {
  __typename: 'Cart' | 'Order'
}

type CustomMutationResult<M> = Pick<MutationResult<M>, 'data' | 'loading' | 'called'>
type CustomMutation<M, V> = (options?: MutationFunctionOptions<M, V> | undefined) => Promise<void | ExecutionResult<M>>

const useMutationWithErrorHandling = <M, V>(
  mutation: DocumentNode,
  options: MutationHookOptions<M, V>,
): [CustomMutation<M, V>, CustomMutationResult<M>] => {
  const context = useCheckoutContext()
  const [mutationFunction, { data, loading, error, called }] = useMutation<M, V>(mutation, {
    ...options,
  })

  // If GraphQL error occurs set global networking error or throw if unknown error is being returned
  useEffect(() => {
    if (!error) return

    if (error.networkError) {
      context.setHasMutationNetworkError(true)
    } else {
      throw new Error(`${error.name} - ${error.message}`)
    }
  }, [error])

  // If data is returned set global error message on ExpectedError
  useEffect(() => {
    if (!data) return

    // Validate if data has a valid response, otherwise throw
    const mutationResult = validateMutationData(data)
    const operationName = Object.keys(data)[0]

    // If an ExpectedError is returned, show it's message or a general error message if the client did not query all fields
    if (mutationResult.__typename === 'ExpectedErrors' && !context.getError(operationName)) {
      const message = mutationResult.errors && mutationResult.errors[0].message
      if (!message) {
        ClientLogger.error('Always query expected errors with all properties "id, path, message" being set')
      }
      context.setError(operationName, message || 'And unexpected error occurred')
    } else if (context.getError(operationName)) {
      context.clearError(operationName)
    }
  }, [data])

  return [mutationFunction, { data, loading, called }]
}

function validateMutationData(data: any): Data | ExpectedErrors {
  const mutationKeys = Object.keys(data)
  if (mutationKeys.length !== 1) {
    throw new Error(`An invalid amount '${mutationKeys.length}' of mutation result has been returned`)
  }
  const mutationName = mutationKeys[0]
  return data[mutationName]
}

export default useMutationWithErrorHandling
